// GeneratePromptModal.jsx — src/quora/GeneratePromptModal.jsx

import React, { useState, useRef } from 'react';
import { Modal, Button, Input, Select, Alert, Divider, Typography, Tag, Space } from 'antd';
import {
  ThunderboltOutlined, SaveOutlined, ReloadOutlined,
  YoutubeOutlined, GlobalOutlined, ArrowLeftOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import axios from '../components/Axios';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// ── Constants ─────────────────────────────────────────────────────────────────

const AI_PROVIDERS = [
  { value: 'openai', label: '🤖 OpenAI',           userKey: 'chatgptApiKey' },
  { value: 'gemini', label: '✨ Google Gemini',     userKey: 'geminiApiKey'  },
  { value: 'claude', label: '🧠 Anthropic Claude',  userKey: 'claudeApiKey'  },
];

const PROVIDER_MODELS = {
  openai: [
    { value: 'gpt-4o',        label: 'GPT-4o',          tag: 'Latest'    },
    { value: 'gpt-4o-mini',   label: 'GPT-4o Mini',     tag: 'Fast'      },
    { value: 'gpt-4.1',       label: 'GPT-4.1',         tag: 'Latest'    },
    { value: 'gpt-4.1-mini',  label: 'GPT-4.1 Mini',    tag: 'Fast'      },
    { value: 'gpt-4.1-nano',  label: 'GPT-4.1 Nano',    tag: 'Cheapest'  },
    { value: 'o3',             label: 'o3',               tag: 'Reasoning' },
    { value: 'o4-mini',        label: 'o4-mini',          tag: 'Reasoning' },
  ],
  gemini: [
    { value: 'gemini-2.5-pro',         label: 'Gemini 2.5 Pro',         tag: 'Best'     },
    { value: 'gemini-2.5-flash',       label: 'Gemini 2.5 Flash',       tag: 'Fast'     },
    { value: 'gemini-2.5-flash-lite',  label: 'Gemini 2.5 Flash-Lite',  tag: 'Cheapest' },
    { value: 'gemini-3.5-flash',       label: 'Gemini 3.5 Flash',       tag: 'Latest'   },
    { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro Preview', tag: 'Preview'  },
    { value: 'gemini-3.1-flash-lite',  label: 'Gemini 3.1 Flash-Lite',  tag: 'Fast'     },
    { value: 'gemini-2.0-flash',       label: 'Gemini 2.0 Flash',       tag: 'Stable'   },
  ],
  claude: [
    { value: 'claude-opus-4-8',    label: 'Claude Opus 4.8',    tag: 'Best'      },
    { value: 'claude-opus-4-7',    label: 'Claude Opus 4.7',    tag: 'Powerful'  },
    { value: 'claude-opus-4-6',    label: 'Claude Opus 4.6',    tag: 'Stable'    },
    { value: 'claude-sonnet-4-6',  label: 'Claude Sonnet 4.6',  tag: 'Balanced'  },
    { value: 'claude-sonnet-4-5',  label: 'Claude Sonnet 4.5',  tag: 'Fast'      },
    { value: 'claude-haiku-4-5',   label: 'Claude Haiku 4.5',   tag: 'Cheapest'  },
  ],
};

const TAG_COLORS = {
  Latest: '#7c3aed', Best: '#059669', Fast: '#0284c7', Cheapest: '#d97706',
  Reasoning: '#dc2626', Preview: '#7c3aed', Balanced: '#0891b2',
  Powerful: '#059669', Stable: '#64748b',
};

const TONES = [
  { value: 'honest_personal', label: '💬 Honest & Personal' },
  { value: 'authoritative',   label: '🎯 Authoritative'     },
  { value: 'conversational',  label: '☕ Conversational'    },
  { value: 'storytelling',    label: '📖 Storytelling'      },
  { value: 'analytical',      label: '📊 Analytical'        },
  { value: 'contrarian',      label: '🔥 Contrarian'        },
];

const WEBSITE_VARS = [
  { label: '{question}',         color: 'blue'    },
  { label: '{keyword}',          color: 'green'   },
  { label: '{cta}',              color: 'orange'  },
  { label: '{basic_info}',       color: 'purple'  },
  { label: '{existing_answers}', color: 'cyan'    },
];

const YOUTUBE_VARS = [
  ...WEBSITE_VARS,
  { label: '{transcript}',  color: 'red'     },
  { label: '{youtube_url}', color: 'volcano' },
];

// ── VariableChips ─────────────────────────────────────────────────────────────

const VariableChips = ({ textareaRef, value, onChange, mode }) => {
  const vars = mode === 'youtube' ? YOUTUBE_VARS : WEBSITE_VARS;
  const insert = (v) => {
    const el = textareaRef?.current?.resizableTextArea?.textArea || textareaRef?.current;
    if (!el) return onChange((value || '') + v);
    const s = el.selectionStart ?? value.length;
    const e = el.selectionEnd   ?? value.length;
    onChange(value.substring(0, s) + v + value.substring(e));
    setTimeout(() => { el.focus(); el.setSelectionRange(s + v.length, s + v.length); }, 0);
  };
  return (
    <Space wrap size={[4, 4]} style={{ marginBottom: 6 }}>
      {vars.map(v => (
        <Tag key={v.label} color={v.color} onClick={() => insert(v.label)}
          style={{ cursor: 'pointer', fontSize: 11, userSelect: 'none', marginRight: 0 }}>
          {v.label}
        </Tag>
      ))}
    </Space>
  );
};

// ── Helper: read API keys from localStorage ───────────────────────────────────

const getSavedKeys = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return {
      openai: !!(user.chatgptApiKey?.trim()),
      gemini: !!(user.geminiApiKey?.trim()),
      claude: !!(user.claudeApiKey?.trim()),
    };
  } catch {
    return { openai: false, gemini: false, claude: false };
  }
};

// ── Defaults ──────────────────────────────────────────────────────────────────

const FORM_DEFAULT = {
  niche: '', audience: '', goal: '',
  mode: 'website', tone: 'honest_personal', extraNotes: '',
  provider: '', specificModel: '',
};

const GEN_DEFAULT = { name: '', description: '', content: '' };

// ── Main Component ────────────────────────────────────────────────────────────

const GeneratePromptModal = ({ visible, onClose, onSaved, userId }) => {
  const [view,       setView]       = useState('form');
  const [form,       setForm]       = useState(FORM_DEFAULT);
  const [generated,  setGenerated]  = useState(GEN_DEFAULT);
  const [generating, setGenerating] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const contentRef = useRef(null);

  const savedKeys   = getSavedKeys();
  const hasKey      = form.provider ? savedKeys[form.provider] : false;
  const modelList   = form.provider ? (PROVIDER_MODELS[form.provider] || []) : [];
  const canGenerate = hasKey && !!form.specificModel;

  const setF = (key) => (val) =>
    setForm(prev => ({ ...prev, [key]: typeof val === 'object' && val?.target ? val.target.value : val }));

  const handleProviderSelect = (providerValue) => {
    const models = PROVIDER_MODELS[providerValue] || [];
    setForm(prev => ({
      ...prev,
      provider:      providerValue,
      specificModel: models.length > 0 ? models[0].value : '',
    }));
  };

  const handleClose = () => {
    setView('form');
    setForm(FORM_DEFAULT);
    setGenerated(GEN_DEFAULT);
    onClose();
  };

  const handleGenerate = async () => {
    if (!form.niche.trim())    return toast.warning('Please enter your niche or topic');
    if (!form.audience.trim()) return toast.warning('Please describe your target audience');
    if (!form.goal.trim())     return toast.warning('Please describe the goal of this prompt');
    if (!form.provider)        return toast.error('Please select an AI provider');
    if (!form.specificModel)   return toast.error('Please select a model');
    if (!hasKey)               return toast.error('No API key for this provider. Add it in Settings → API Keys.');

    setGenerating(true);
    try {
      const res = await axios.post('/api/generate-prompt', {
        userId,
        niche:         form.niche,
        audience:      form.audience,
        goal:          form.goal,
        mode:          form.mode,
        tone:          form.tone,
        extraNotes:    form.extraNotes,
        provider:      form.provider,
        specificModel: form.specificModel,
      });

      if (res.data.success) {
        setGenerated(res.data.data);
        setView('review');
        toast.success('✨ Prompt generated! Review and save it.');
      } else {
        toast.error(res.data.error || 'Failed to generate prompt');
      }
    } catch (err) {
      // err.response?.data?.error — backend ne json error diya (4xx/5xx)
      // err.message — network error ya SDK level error
      toast.error(err.response?.data?.error || err.message || 'Failed to generate prompt. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generated.name.trim())    return toast.warning('Please enter a prompt name');
    if (!generated.content.trim()) return toast.warning('Prompt content cannot be empty');

    setSaving(true);
    try {
      const res = await axios.post('/api/prompt-library', {
        userId,
        name:        generated.name.trim(),
        description: generated.description.trim(),
        content:     generated.content.trim(),
        mode:        form.mode,
      });
      if (res.data.success) {
        toast.success('✅ Prompt saved to library!');
        onSaved(res.data.data);
        handleClose();
      } else {
        toast.error(res.data.error || 'Failed to save prompt');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to save prompt. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── AI Selection ──────────────────────────────────────────────────────────

  const renderAISelection = () => (
    <div style={{ marginTop: 4 }}>
      <Text strong style={{ display: 'block', marginBottom: 10 }}>
        Which AI should generate this prompt?
      </Text>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        {AI_PROVIDERS.map(p => {
          const hasThisKey = savedKeys[p.value];
          const isSelected = form.provider === p.value;
          return (
            <div key={p.value} onClick={() => handleProviderSelect(p.value)} style={{
              flex: 1, borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
              border:      `2px solid ${isSelected ? '#7c3aed' : hasThisKey ? '#d9d9d9' : '#ffd591'}`,
              background:  isSelected ? '#f5f3ff' : hasThisKey ? '#fff' : '#fffbe6',
              boxShadow:   isSelected ? '0 0 0 3px rgba(124,58,237,0.12)' : 'none',
              transition: 'all 0.2s',
            }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? '#7c3aed' : '#333' }}>
                {p.label}
              </div>
              <div style={{ fontSize: 11, marginTop: 3, color: hasThisKey ? '#52c41a' : '#faad14' }}>
                {hasThisKey ? '✓ API key saved' : '⚠ No key — add in Settings'}
              </div>
            </div>
          );
        })}
      </div>

      {form.provider && (
        <div style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 14px', marginBottom: 8, animation: 'fadeIn 0.2s ease' }}>
          <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
            Select Model
            <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 11, marginLeft: 8 }}>
              (all models below support Structured Outputs)
            </Text>
          </Text>
          <Select value={form.specificModel} onChange={setF('specificModel')}
            style={{ width: '100%' }} placeholder="Select a model..." size="middle">
            {modelList.map(m => (
              <Option key={m.value} value={m.value}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 500 }}>{m.label}</span>
                  <span style={{
                    fontSize: 10, padding: '1px 7px', borderRadius: 10,
                    background: TAG_COLORS[m.tag] || '#64748b', color: '#fff',
                    fontWeight: 600, letterSpacing: '0.3px',
                  }}>
                    {m.tag}
                  </span>
                </div>
              </Option>
            ))}
          </Select>

          {form.specificModel && (() => {
            const selected = modelList.find(m => m.value === form.specificModel);
            return selected ? (
              <div style={{ marginTop: 8, fontSize: 12, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircleOutlined />
                <span>Using <strong>{selected.label}</strong><span style={{ color: '#888', marginLeft: 4 }}>({form.specificModel})</span></span>
              </div>
            ) : null;
          })()}
        </div>
      )}

      {!hasKey && form.provider && (
        <Alert type="warning" style={{ marginTop: 8, fontSize: 12 }} message={
          <span>
            No API key for this provider.{' '}
            <a href="/api-settings" style={{ color: '#7c3aed', fontWeight: 600 }}>Go to Settings → API Keys</a>{' '}
            to add one.
          </span>
        } />
      )}
    </div>
  );

  // ── Form View ─────────────────────────────────────────────────────────────

  const renderForm = () => (
    <div>
      <Alert showIcon type="info" style={{ marginBottom: 20, fontSize: 13 }}
        message="Describe your niche and needs — AI will pick the best technique and build a perfect Quora prompt for you." />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Niche / Topic <Text type="danger">*</Text></Text>
          <Input value={form.niche} onChange={setF('niche')} placeholder="e.g. SaaS tools, fitness, crypto..." />
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Target Audience <Text type="danger">*</Text></Text>
          <Input value={form.audience} onChange={setF('audience')} placeholder="e.g. startup founders, beginners..." />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>Goal / Purpose <Text type="danger">*</Text></Text>
        <Input value={form.goal} onChange={setF('goal')} placeholder="e.g. drive traffic to my website, promote my YouTube channel..." />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Mode</Text>
          <Select value={form.mode} onChange={setF('mode')} style={{ width: '100%' }}>
            <Option value="website"><GlobalOutlined style={{ color: '#1890ff', marginRight: 6 }} />Website Mode</Option>
            <Option value="youtube"><YoutubeOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />YouTube Mode</Option>
          </Select>
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4, display: 'block' }}>
            {form.mode === 'youtube' ? 'Includes {transcript} and {youtube_url}' : 'Includes {keyword}, {cta}, {basic_info}'}
          </Text>
        </div>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>Writing Tone</Text>
          <Select value={form.tone} onChange={setF('tone')} style={{ width: '100%' }}>
            {TONES.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
          </Select>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>
          Extra Instructions <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>(optional)</Text>
        </Text>
        <TextArea value={form.extraNotes} onChange={setF('extraNotes')} rows={3}
          placeholder="e.g. avoid bullet points, mention my product organically, keep under 400 words..." />
      </div>

      <Divider style={{ margin: '16px 0' }} />
      {renderAISelection()}
    </div>
  );

  // ── Review View ───────────────────────────────────────────────────────────

  const renderReview = () => (
    <div>
      <Alert showIcon icon={<CheckCircleOutlined />} type="success" style={{ marginBottom: 20, fontSize: 13 }}
        message="Prompt generated! Review and edit below, then save to your library." />

      <div style={{ marginBottom: 14 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>Prompt Name <Text type="danger">*</Text></Text>
        <Input value={generated.name} onChange={e => setGenerated(p => ({ ...p, name: e.target.value }))} placeholder="Prompt name..." />
      </div>

      <div style={{ marginBottom: 14 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>
          Description <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>(optional)</Text>
        </Text>
        <Input value={generated.description} onChange={e => setGenerated(p => ({ ...p, description: e.target.value }))} placeholder="Short description..." />
      </div>

      <div style={{ marginBottom: 14 }}>
        <Text strong style={{ display: 'block', marginBottom: 6 }}>Mode</Text>
        {form.mode === 'youtube'
          ? <Tag color="red"  icon={<YoutubeOutlined />}>YouTube Mode</Tag>
          : <Tag color="blue" icon={<GlobalOutlined />}>Website Mode</Tag>}
        <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>(go back to change)</Text>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <Text strong style={{ display: 'block', marginBottom: 6 }}>Prompt Content <Text type="danger">*</Text></Text>
      <VariableChips textareaRef={contentRef} value={generated.content} onChange={v => setGenerated(p => ({ ...p, content: v }))} mode={form.mode} />
      <TextArea ref={contentRef} value={generated.content} rows={18}
        onChange={e => setGenerated(p => ({ ...p, content: e.target.value }))}
        style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 6 }} />
      <Text type="secondary" style={{ fontSize: 11, marginTop: 6, display: 'block' }}>
        Variables in <code style={{ background: '#f0f0f0', padding: '0 3px', borderRadius: 3 }}>{'{curly_braces}'}</code> are replaced automatically when generating answers.
      </Text>
    </div>
  );

  // ── Footer ────────────────────────────────────────────────────────────────

  const footer = view === 'form' ? [
    <Button key="cancel" onClick={handleClose}>Cancel</Button>,
    <Button key="generate" type="primary" icon={<ThunderboltOutlined />}
      loading={generating} onClick={handleGenerate} disabled={!canGenerate}
      style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}>
      {generating ? 'Generating…' : '✨ Generate Prompt'}
    </Button>,
  ] : [
    <Button key="back" icon={<ArrowLeftOutlined />} onClick={() => setView('form')} disabled={saving}>← Edit Inputs</Button>,
    <Button key="regen" icon={<ReloadOutlined />} disabled={saving || generating}
      onClick={() => { setView('form'); setTimeout(handleGenerate, 50); }}>Regenerate</Button>,
    <Button key="save" type="primary" icon={<SaveOutlined />} loading={saving}
      onClick={handleSave} style={{ backgroundColor: '#7c3aed', borderColor: '#7c3aed' }}>
      Save to Library
    </Button>,
  ];

  return (
    <Modal open={visible} onCancel={handleClose} width={800} footer={footer}
      destroyOnClose={false}
      bodyStyle={{ maxHeight: '75vh', overflowY: 'auto', padding: '20px 24px' }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThunderboltOutlined style={{ color: '#7c3aed' }} />
          <span>{view === 'form' ? '✨ Generate New Prompt with AI' : '📝 Review & Save Generated Prompt'}</span>
        </div>
      }>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      {view === 'form'   && renderForm()}
      {view === 'review' && renderReview()}
    </Modal>
  );
};

export default GeneratePromptModal;